import { NavLink, useParams } from "react-router";

import { Box, ToggleButton, ToggleButtonGroup } from "@mui/material";
import {
  WarningLabel,
  useAssetInventoryHasWarning,
  useBankruptcyChecklistHasWarning,
  useHasInactiveClaim,
} from "@shared";

import { useActiveDebtSettlementTab } from "@/pages/clients/client/hooks/useClientTabs";
import { ClientByIdParams, DebtSettlementSubTab, clientPath } from "@/router/ClientRoutes";
import { useTranslation } from "@/translations";

export const DebtSettlementHeader = () => {
  const { t } = useTranslation();
  const { id } = useParams<ClientByIdParams>();
  const activeSubTab = useActiveDebtSettlementTab();
  const hasInactiveClaim = useHasInactiveClaim(id);
  const hasChecklistWarning = useBankruptcyChecklistHasWarning(id);
  const hasAssetInventoryWarning = useAssetInventoryHasWarning(id);

  if (!id) {
    return null;
  }

  return (
    <Box
      display={"flex"}
      justifyContent={"flex-end"}
      bgcolor={(theme) => theme.palette.fsw["blue-5"]}
      border="1px solid"
      borderTop="none"
      borderColor={(theme) => theme.palette.fsw["blue-10"]}
      borderRadius={"4px 0 4px 4px"}
      mr={0.5}
    >
      <ToggleButtonGroup
        color="primary"
        exclusive
        value={activeSubTab}
        sx={{
          "& .MuiToggleButton-root.Mui-selected": {
            border: "none",
          },
        }}
      >
        <ToggleButton
          value={DebtSettlementSubTab.AssetInventory}
          component={NavLink}
          to={clientPath.byDebtSettlementTab({ id, tab: DebtSettlementSubTab.AssetInventory })}
        >
          <WarningLabel
            warning={
              hasAssetInventoryWarning ? t("clients.client.debtSettlement.assetInventory.sectionIncomplete") : undefined
            }
          >
            {t("clients.client.tabs.debtSettlement.assetList")}
          </WarningLabel>
        </ToggleButton>
        <ToggleButton
          value={DebtSettlementSubTab.Claims}
          component={NavLink}
          to={clientPath.byDebtSettlementTab({ id, tab: DebtSettlementSubTab.Claims })}
        >
          <WarningLabel warning={hasInactiveClaim ? t("claims.table.inactiveCreditor") : undefined}>
            {t("clients.client.tabs.debtSettlement.claims")}
          </WarningLabel>
        </ToggleButton>
        <ToggleButton
          value={DebtSettlementSubTab.BankruptcyChecklist}
          component={NavLink}
          to={clientPath.byDebtSettlementTab({ id, tab: DebtSettlementSubTab.BankruptcyChecklist })}
        >
          <WarningLabel
            warning={
              hasChecklistWarning ? t("clients.client.debtSettlement.bankruptcyChecklist.statusWarning") : undefined
            }
          >
            {t("clients.client.tabs.debtSettlement.bankruptcyChecklist")}
          </WarningLabel>
        </ToggleButton>
        <ToggleButton
          value={DebtSettlementSubTab.Compensation}
          component={NavLink}
          to={clientPath.byDebtSettlementTab({ id, tab: DebtSettlementSubTab.Compensation })}
        >
          {t("clients.client.tabs.debtSettlement.compensation")}
        </ToggleButton>
        <ToggleButton
          value={DebtSettlementSubTab.PersonalBankruptcyPetition}
          component={NavLink}
          to={clientPath.byDebtSettlementTab({ id, tab: DebtSettlementSubTab.PersonalBankruptcyPetition })}
        >
          {t("clients.client.tabs.debtSettlement.personalBankruptcyPetition")}
        </ToggleButton>
        <ToggleButton
          value={DebtSettlementSubTab.SrvProceeding}
          component={NavLink}
          to={clientPath.byDebtSettlementTab({ id, tab: DebtSettlementSubTab.SrvProceeding })}
          sx={{ mr: 0 }}
        >
          {t("clients.client.tabs.debtSettlement.srvProceeding")}
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
};
